import { editorial, media } from '@/constants/media'

export interface LookbookChapter {
    id: string
    numeral: string
    name: string
    words: [string, string]
    caption: string
    image: string
    alt: string
    shape: 'circle' | 'slit' | 'pill'
    focus?: string
}

export const lookbookChapters: Array<LookbookChapter> = [
    {
        id: 'concrete',
        numeral: 'I',
        name: 'Concrete',
        words: ['Con', 'crete'],
        caption: 'Chapter I — Shot at dawn, Tempelhof',
        image: media.concreteArcCoat,
        alt: "A model in a cream rain coat and wide trousers walking beneath a curved concrete roof",
        shape: 'circle',
        focus: 'object-[72%_50%]'
    },
    {
        id: 'night-shift',
        numeral: 'II',
        name: 'Night Shift',
        words: ['Night', 'Shift'],
        caption: 'Chapter II — 2 a.m. to 5 a.m., Berlin-Mitte',
        image: editorial.nightShiftUmbrella,
        alt: "A lone figure under a black umbrella crossing wet cobblestones at night",
        shape: 'slit',
        focus: 'object-[50%_72%]'
    },
    {
        id: 'soft-armor',
        numeral: 'III',
        name: 'Soft Armor',
        words: ['Soft', 'Armor'],
        caption: 'Chapter III — Heavy knits, quiet shapes',
        image: editorial.softArmorColourBlock,
        alt: "A model in an oversized colour-block knit and ribbed beanie, seen from behind",
        shape: 'pill',
        focus: 'object-[50%_35%]'
    }
]

export interface Look {
    number: string
    chapter: string
    image: string
    alt: string
}

export const looks: Array<Look> = [
    { number: '01', chapter: 'I — Concrete', image: media.concreteRoomBlack, alt: "A model in an all-black outfit standing in a sunlit concrete room" },
    { number: '02', chapter: 'I — Concrete', image: media.wetHairConcrete, alt: "A model with wet hair in a black shell jacket against a concrete wall" },
    { number: '03', chapter: 'I — Concrete', image: media.concreteStepsSeated, alt: "A model in a black shell and cargo trousers seated on concrete steps" },
    { number: '04', chapter: 'II — Night Shift', image: media.hoodedCoastStorm, alt: "A hooded figure in black facing a stormy sea" },
    { number: '05', chapter: 'II — Night Shift', image: media.overcastSilhouette, alt: "A model in a black shell jacket silhouetted against a stormy sky" },
    { number: '06', chapter: 'II — Night Shift', image: media.ledgeOvercast, alt: "A model in black seated on a concrete ledge under an overcast sky" },
    { number: '07', chapter: 'III — Soft Armor', image: media.blackShellPortrait, alt: "A model in a high-collared black shell jacket" },
    { number: '08', chapter: 'III — Soft Armor', image: media.beanieVisorPortrait, alt: "A model in a black beanie, sunglasses and hooded shell" },
    { number: '09', chapter: 'III — Soft Armor', image: media.rainShellHood, alt: "A hooded technical shell beaded with rain" }
]

export const lookbookCredits = [
    { code: 'PH', name: 'Lena Vogt', role: 'Photography', image: editorial.creditPhotography, alt: "Cameras lined up in front of a paper backdrop in a daylight studio" },
    { code: 'ST', name: 'Kofi Mensah', role: 'Styling', image: editorial.creditStyling, alt: "A styling rail of garments on wooden hangers" },
    { code: 'CA', name: 'Ines Duarte', role: 'Casting', image: editorial.creditCasting, alt: "A model in a grey knit standing in a bare room" },
    { code: 'HM', name: 'Yuki Sato', role: 'Hair & make-up', image: editorial.creditHairMakeUp, alt: "A make-up brush sweeping across a model's cheek" },
    { code: 'LO', name: 'Tempelhof Field', role: 'Location', image: editorial.creditLocation, alt: "A hangar and open apron under a wide sky" }
]

export const timeline = [
    { year: '2019', title: 'A rail in a Kreuzberg basement', detail: "Mara Okonkwo sells twelve hand-cut parkas to friends. All twelve come back for repairs. None are returned." },
    { year: '2020', title: 'The first numbered run', detail: "Essentials 01 — three pieces, 300 of each. It sells out in nine hours." },
    { year: '2022', title: 'Atelier in Porto', detail: "We move production to a family-run factory we can visit by train." },
    { year: '2023', title: 'Repairs for life', detail: "Every Elio garment ever made becomes repairable, for free, forever." },
    { year: '2024', title: 'Tokyo Aoyama', detail: "Our second flagship opens behind a steel door with no signage." },
    { year: '2026', title: 'Essentials 04', detail: "Twelve pieces. One wardrobe. You’re looking at it." }
]

export const team = [
    { name: 'Mara Okonkwo', role: 'Founder & Creative Director', image: media.wetHairConcrete, alt: "Mara Okonkwo in a black shell jacket against a concrete wall" },
    { name: 'Jonas Lindqvist', role: 'Head of Atelier', image: media.concreteStepsSeated, alt: "Jonas Lindqvist in a black shell and cargo trousers seated on concrete steps" },
    { name: 'Aiko Tanaka', role: 'Stores & Community', image: media.blackShellPortrait, alt: "Aiko Tanaka in a high-collared black shell jacket" },
    { name: 'Kofi Mensah', role: 'Pattern Cutter', image: media.beanieVisorPortrait, alt: "Kofi Mensah in a black beanie, sunglasses and hooded shell" }
]

export const values = ['Fewer', 'Heavier', 'Longer', 'Repairable']

export const faqs = [
    {
        question: 'How do free repairs work?',
        answer: "Bring or send any Elio piece to one of our stores. We repair seams, zips, buttons and small tears for free — for the life of the garment."
    },
    {
        question: 'What does a numbered run mean?',
        answer: "Each style is produced once, in a run of 300. Every garment carries its number on the inside label. Sold-out styles do not return."
    },
    {
        question: 'Do you ship worldwide?',
        answer: "Yes. Carbon-neutral express shipping to 60+ countries, usually 2–4 business days. Duties are included at checkout."
    },
    {
        question: 'Can I return an item?',
        answer: "Unworn pieces with the numbered tag attached can be returned within 30 days for a full refund."
    },
    {
        question: 'How do I find my size?',
        answer: "Our pieces are cut generously. Every product page lists the fit and a size guide — or write to us and a pattern cutter will reply."
    }
]

export const contactTopics = ['An order', 'A repair', 'Press & collaborations', 'Something else']
